import React, { useEffect, useMemo, useRef, useState } from "react";

import { ChartData, ChartOptions, TooltipItem, Chart as TypeChart } from "chart.js/types/index.esm";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { formatNumber } from "../../../../../utils/table";

export interface ApiStateChartProps {
  successfulPercent: number;
  errorsPercent: number;
  zerosPercent: number;
  timeoutsPercent: number;
}

export const ApiStateChart: React.FC<ApiStateChartProps> = (props) => {
  const { t } = useTranslation("api_state_widget");
  const { successfulPercent, errorsPercent, zerosPercent, timeoutsPercent } = props;

  const chartRef = useRef<TypeChart<"doughnut", number[]>>();
  const [options, setOptions] = useState<ChartOptions<"doughnut">>({});
  const [data, setData] = useState<ChartData<"doughnut", number[]>>({ datasets: [] });

  useEffect(
    () =>
      setOptions({
        responsive: true,
        maintainAspectRatio: true,
        cutout: "42%",
        circumference: 359.99,
        layout: { padding: { top: 20 } },
        plugins: {
          legend: {
            position: "bottom",
            align: "center",
            title: { display: true, position: "center" },
            labels: {
              boxWidth: 8,
              padding: 8,
              font: { size: 12, family: "Open sans", weight: "600" },
              usePointStyle: true,
            },
          },
          tooltip: {
            position: "nearest",
            caretPadding: (context) => {
              const meta: any = context.chart.getSortedVisibleDatasetMetas()[0];
              const item: any = context.tooltipItems[0];
              if (meta && item) {
                const alpha = 360 * (item.raw / meta.total);
                return alpha > 16 ? 23 : 0;
              }
              return 0;
            },
            callbacks: {
              label: (tooltipItem: TooltipItem<"doughnut">): string | string[] => {
                const value: any = tooltipItem.dataset.data[tooltipItem.dataIndex];
                return `${tooltipItem.label}: ${formatNumber(value)} %`;
              },
            },
          },
          datalabels: {
            display: (context) => {
              const meta: any = context.chart.getSortedVisibleDatasetMetas()[0];
              const value: any = context.dataset.data[context.dataIndex];
              if (meta) {
                const alpha = 360 * (value / meta.total);
                return alpha > 16;
              }
              return false;
            },
            formatter: (value: number) => `${formatNumber(value)} %`,
            color: "white",
            font: { weight: "bold" },
          },
        },
      }),
    []
  );

  useEffect(() => {
    setData({
      labels: [
        t("chart.labels.successful"),
        t("chart.labels.errors"),
        t("chart.labels.timeouts"),
        t("chart.labels.zeros"),
      ],
      datasets: [
        {
          borderWidth: 1,
          data: [successfulPercent, errorsPercent, timeoutsPercent, zerosPercent],
          backgroundColor: ["#35C768", "#F54759", "#FF6F32", "#0C73FE"],
        },
      ],
    });
  }, [t, successfulPercent, errorsPercent, zerosPercent, timeoutsPercent]);

  useEffect(() => {
    const chart = chartRef.current;
    if (chart !== undefined) {
      chart.options = options;
      chart.data = data;
      chart.update("none");
    }
  }, [data, options]);

  return useMemo(() => {
    return (
      <Doughnut
        ref={chartRef}
        data={{ datasets: [] }}
        plugins={[ChartDataLabels]}
        type="doughnut"
        style={{ maxHeight: "375px" }}
      />
    );
  }, []);
};
